// The UI kit: every page builds from these, so spacing, radii, focus rings and dropdowns look the
// same everywhere. Interactive primitives (select, menu, checkbox, dialog) come from Radix for
// keyboard and screen-reader support; this file only styles them.
import {
  Check,
  ChevronDown,
  CircleAlert,
  Inbox,
  LoaderCircle,
  type LucideIcon,
  Search,
  X,
} from "lucide-react";
import {
  Checkbox as CheckboxPrimitive,
  Dialog as DialogPrimitive,
  DropdownMenu as MenuPrimitive,
  Select as SelectPrimitive,
  Switch as SwitchPrimitive,
  Tabs as TabsPrimitive,
} from "radix-ui";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export const cn = (...c: (string | false | null | undefined)[]) => twMerge(c.filter(Boolean).join(" "));

const focusRing = "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600";

// ── Buttons ──────────────────────────────────────────────────────────────

type Variant = "primary" | "secondary" | "ghost" | "danger";
const variants: Record<Variant, string> = {
  primary: "bg-brand-700 text-white shadow-xs hover:bg-brand-800",
  secondary: "bg-white text-slate-700 shadow-xs ring-1 ring-inset ring-slate-300 hover:bg-slate-50",
  ghost: "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
  danger: "bg-red-600 text-white shadow-xs hover:bg-red-700",
};
const sizes = {
  sm: "h-8 gap-1.5 px-2.5 text-xs [&_svg]:size-3.5",
  md: "h-9 gap-2 px-3.5 text-sm [&_svg]:size-4",
};

export function Button({
  variant = "primary",
  size = "md",
  icon: Icon,
  loading,
  className,
  children,
  disabled,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: keyof typeof sizes;
  icon?: LucideIcon;
  loading?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={cn(
        "inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-lg font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
        focusRing,
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    >
      {loading ? <LoaderCircle className="animate-spin" /> : Icon && <Icon />}
      {children}
    </button>
  );
}

// ── Layout blocks ────────────────────────────────────────────────────────

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section
      className={cn("overflow-hidden rounded-xl bg-white shadow-xs ring-1 ring-slate-200/80", className)}
    >
      {children}
    </section>
  );
}

export function CardHeader({
  title,
  description,
  icon: Icon,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  icon?: LucideIcon;
  actions?: ReactNode;
}) {
  return (
    <div className="flex min-h-14 flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
      <div className="flex min-w-0 items-center gap-2.5">
        {Icon && <Icon className="size-4 shrink-0 text-slate-400" />}
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-slate-900">{title}</h2>
          {description && <p className="text-xs text-slate-500">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  back,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  back?: ReactNode;
}) {
  return (
    <div className="mb-6">
      {back && <div className="mb-3">{back}</div>}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

// ── Status & feedback ────────────────────────────────────────────────────

export type Tone = "neutral" | "green" | "amber" | "red" | "blue" | "brand";
const tones: Record<Tone, { badge: string; dot: string }> = {
  neutral: { badge: "bg-slate-100 text-slate-700 ring-slate-500/10", dot: "bg-slate-400" },
  green: { badge: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", dot: "bg-emerald-500" },
  amber: { badge: "bg-amber-50 text-amber-800 ring-amber-600/20", dot: "bg-amber-500" },
  red: { badge: "bg-red-50 text-red-700 ring-red-600/15", dot: "bg-red-500" },
  blue: { badge: "bg-sky-50 text-sky-700 ring-sky-600/20", dot: "bg-sky-500" },
  brand: { badge: "bg-brand-50 text-brand-800 ring-brand-600/20", dot: "bg-brand-600" },
};

export function Badge({
  tone = "neutral",
  dot,
  children,
}: {
  tone?: Tone;
  dot?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
        tones[tone].badge,
      )}
    >
      {dot && <span className={cn("size-1.5 rounded-full", tones[tone].dot)} />}
      {children}
    </span>
  );
}

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 px-5 py-12 text-sm text-slate-500" role="status">
      <LoaderCircle className="size-4 animate-spin text-brand-600" />
      {label}
    </div>
  );
}

export function ErrorNote({ error, className }: { error: unknown; className?: string }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2.5 text-sm text-red-700 ring-1 ring-inset ring-red-200",
        className,
      )}
      role="alert"
    >
      <CircleAlert className="mt-0.5 size-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

export function Empty({
  icon: Icon = Inbox,
  title,
  children,
}: {
  icon?: LucideIcon;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-5 py-12 text-center">
      <div className="mb-3 flex size-10 items-center justify-center rounded-full bg-slate-100">
        <Icon className="size-5 text-slate-400" />
      </div>
      <div className="text-sm font-medium text-slate-900">{title}</div>
      {children && <div className="mt-1 text-sm text-slate-500">{children}</div>}
    </div>
  );
}

// ── Form controls ────────────────────────────────────────────────────────

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the control is passed in as children
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>
      {children}
      {hint && <span className="mt-1.5 block text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

const control =
  "h-9 w-full rounded-lg border-0 bg-white px-3 text-sm text-slate-900 shadow-xs ring-1 ring-inset ring-slate-300 transition-shadow placeholder:text-slate-400 hover:ring-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500";

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(control, className)} {...props} />;
}

export function SearchInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className={cn("relative", className)}>
      <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
      <input type="search" className={cn(control, "pl-9")} {...props} />
    </div>
  );
}

export interface Option {
  value: string;
  label: ReactNode;
  /** Shown in the trigger instead of `label` when given (e.g. a shorter text). */
  short?: ReactNode;
  disabled?: boolean;
}
export interface OptionGroup {
  label: string;
  options: Option[];
}

// Radix reserves "" for "no value", so an option meaning "none/all" travels as this sentinel.
const EMPTY = "__empty__";

/** Styled dropdown. `value` "" selects the option whose value is "" (e.g. "All statuses"). */
export function Select({
  value,
  onValueChange,
  options,
  groups,
  placeholder = "Select…",
  className,
  size = "md",
  disabled,
  "aria-label": ariaLabel,
  tone,
}: {
  value: string;
  onValueChange: (v: string) => void;
  options?: Option[];
  groups?: OptionGroup[];
  placeholder?: string;
  className?: string;
  size?: "sm" | "md";
  disabled?: boolean;
  "aria-label"?: string;
  /** Highlight the trigger, e.g. amber for "needs attention". */
  tone?: "amber";
}) {
  const all = [...(options ?? []), ...(groups ?? []).flatMap((g) => g.options)];
  const selected = all.find((o) => o.value === value);
  const item = (o: Option) => (
    <SelectPrimitive.Item
      key={o.value || EMPTY}
      value={o.value || EMPTY}
      disabled={o.disabled}
      className="relative flex h-8 cursor-pointer items-center rounded-md pr-8 pl-2.5 text-sm text-slate-700 outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-slate-100 data-[highlighted]:text-slate-900 data-[state=checked]:font-medium data-[state=checked]:text-brand-800"
    >
      <SelectPrimitive.ItemText>{o.label}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className="absolute right-2.5 inline-flex">
        <Check className="size-4 text-brand-700" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );

  return (
    <SelectPrimitive.Root
      value={selected ? value || EMPTY : ""}
      onValueChange={(v) => onValueChange(v === EMPTY ? "" : v)}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        className={cn(
          control,
          "inline-flex items-center justify-between gap-2 text-left data-[placeholder]:text-slate-400 data-[state=open]:ring-2 data-[state=open]:ring-brand-600",
          size === "sm" && "h-8 text-xs",
          tone === "amber" && "bg-amber-50/60 ring-amber-300 hover:ring-amber-400",
          className,
        )}
      >
        <span className="truncate">
          <SelectPrimitive.Value placeholder={placeholder}>
            {selected?.short ?? selected?.label}
          </SelectPrimitive.Value>
        </span>
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="size-4 shrink-0 text-slate-400" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          collisionPadding={8}
          className="z-50 max-h-[min(var(--radix-select-content-available-height),22rem)] min-w-[var(--radix-select-trigger-width)] animate-pop-in overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-slate-200"
        >
          <SelectPrimitive.Viewport className="p-1">
            {options?.map(item)}
            {groups?.map((g, i) => (
              <SelectPrimitive.Group key={g.label}>
                {(i > 0 || options?.length) && (
                  <SelectPrimitive.Separator className="my-1 h-px bg-slate-100" />
                )}
                <SelectPrimitive.Label className="px-2.5 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  {g.label}
                </SelectPrimitive.Label>
                {g.options.map(item)}
              </SelectPrimitive.Group>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

export function Checkbox({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the Radix checkbox is a button with role="checkbox"
    <label className="flex cursor-pointer items-start gap-3 text-sm">
      <CheckboxPrimitive.Root
        checked={checked}
        onCheckedChange={(v) => onChange(v === true)}
        className={cn(
          "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded bg-white shadow-xs ring-1 ring-inset ring-slate-300 transition-colors data-[state=checked]:bg-brand-700 data-[state=checked]:ring-brand-700",
          focusRing,
        )}
      >
        <CheckboxPrimitive.Indicator>
          <Check className="size-3 text-white" strokeWidth={3} />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      <span>
        <span className="block font-medium text-slate-700">{label}</span>
        {description && <span className="block text-xs text-slate-500">{description}</span>}
      </span>
    </label>
  );
}

// ── Dropdown menu ────────────────────────────────────────────────────────

export const Menu = MenuPrimitive.Root;
export const MenuTrigger = MenuPrimitive.Trigger;

export function MenuContent({
  children,
  align = "start",
  side = "bottom",
  className,
}: {
  children: ReactNode;
  align?: "start" | "end" | "center";
  side?: "top" | "bottom" | "right" | "left";
  className?: string;
}) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Content
        align={align}
        side={side}
        sideOffset={6}
        collisionPadding={8}
        className={cn(
          "z-50 min-w-56 animate-pop-in rounded-lg bg-white p-1 shadow-lg ring-1 ring-slate-200",
          className,
        )}
      >
        {children}
      </MenuPrimitive.Content>
    </MenuPrimitive.Portal>
  );
}

export function MenuItem({
  icon: Icon,
  children,
  onSelect,
  checked,
  tone,
  disabled,
}: {
  icon?: LucideIcon;
  children: ReactNode;
  onSelect?: () => void;
  checked?: boolean;
  tone?: "danger";
  disabled?: boolean;
}) {
  return (
    <MenuPrimitive.Item
      onSelect={onSelect}
      disabled={disabled}
      className={cn(
        "flex h-9 cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-sm text-slate-700 outline-none select-none data-[highlighted]:bg-slate-100 data-[highlighted]:text-slate-900 data-[disabled]:opacity-50",
        tone === "danger" && "text-red-600 data-[highlighted]:bg-red-50 data-[highlighted]:text-red-700",
      )}
    >
      {Icon && <Icon className="size-4 shrink-0 opacity-70" />}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {checked && <Check className="size-4 shrink-0 text-brand-700" />}
    </MenuPrimitive.Item>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <MenuPrimitive.Label className="px-2.5 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
      {children}
    </MenuPrimitive.Label>
  );
}

export function MenuSeparator() {
  return <MenuPrimitive.Separator className="my-1 h-px bg-slate-100" />;
}

// ── Tables ───────────────────────────────────────────────────────────────

type Align = "left" | "right" | "center";
const alignClass: Record<Align, string> = { left: "text-left", right: "text-right", center: "text-center" };
export type Column = string | { label: ReactNode; align?: Align; className?: string };

export function Table({ columns, children }: { columns: Column[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50/70">
          <tr className="border-b border-slate-200">
            {columns.map((c, i) => {
              const col = typeof c === "string" ? { label: c } : c;
              return (
                <th
                  // biome-ignore lint/suspicious/noArrayIndexKey: static header
                  key={i}
                  scope="col"
                  className={cn(
                    "h-10 px-5 text-xs font-medium whitespace-nowrap text-slate-500",
                    alignClass[col.align ?? "left"],
                    col.className,
                  )}
                >
                  {col.label}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  );
}

export function Tr({ children, className }: { children: ReactNode; className?: string }) {
  return <tr className={cn("transition-colors hover:bg-slate-50/80", className)}>{children}</tr>;
}

export function Td({
  children,
  align = "left",
  className,
  nowrap,
}: {
  children?: ReactNode;
  align?: Align;
  className?: string;
  nowrap?: boolean;
}) {
  return (
    <td
      className={cn(
        "h-14 px-5 py-2 align-middle text-slate-700",
        alignClass[align],
        align === "right" && "tabular",
        nowrap && "whitespace-nowrap",
        className,
      )}
    >
      {children}
    </td>
  );
}

/** Primary line plus a muted second line, for table cells. */
export function TwoLine({ primary, secondary }: { primary: ReactNode; secondary?: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="truncate">{primary}</div>
      {secondary && <div className="truncate text-xs text-slate-500">{secondary}</div>}
    </div>
  );
}

// ── Stats ────────────────────────────────────────────────────────────────

export function Stat({
  label,
  value,
  hint,
  icon: Icon,
  tone = "neutral",
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: LucideIcon;
  tone?: "neutral" | "positive" | "negative";
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-slate-500">{label}</span>
        {Icon && (
          <span className="flex size-8 items-center justify-center rounded-lg bg-brand-50">
            <Icon className="size-4 text-brand-700" />
          </span>
        )}
      </div>
      <div
        className={cn(
          "mt-2 text-2xl font-semibold tracking-tight tabular",
          tone === "negative" ? "text-red-600" : "text-slate-900",
        )}
      >
        {value}
      </div>
      {hint && <div className="mt-0.5 text-xs text-slate-400">{hint}</div>}
    </Card>
  );
}

// ── Switch & modal ───────────────────────────────────────────────────────

export function Switch({
  checked,
  onChange,
  disabled,
  "aria-label": ariaLabel,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  "aria-label"?: string;
}) {
  return (
    <SwitchPrimitive.Root
      checked={checked}
      onCheckedChange={onChange}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        "relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full bg-slate-200 transition-colors data-[state=checked]:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50",
        focusRing,
      )}
    >
      <SwitchPrimitive.Thumb className="block size-4 translate-x-0.5 rounded-full bg-white shadow-sm transition-transform data-[state=checked]:translate-x-[18px]" />
    </SwitchPrimitive.Root>
  );
}

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[2px]" />
        <DialogPrimitive.Content className="fixed top-1/2 left-1/2 z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 animate-pop-in rounded-xl bg-white shadow-xl ring-1 ring-slate-200 focus:outline-none">
          <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
            <div>
              <DialogPrimitive.Title className="text-base font-semibold text-slate-900">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-0.5 text-sm text-slate-500">
                  {description}
                </DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close
              className="-mr-1.5 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Close"
            >
              <X className="size-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="space-y-5 px-5 py-5">{children}</div>
          {footer && (
            <div className="flex justify-end gap-2 rounded-b-xl border-t border-slate-100 bg-slate-50/60 px-5 py-3">
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

// ── Tabs ─────────────────────────────────────────────────────────────────

export const Tabs = TabsPrimitive.Root;
export const TabPanel = TabsPrimitive.Content;

export function TabList({
  tabs,
}: {
  tabs: { value: string; label: string; icon?: LucideIcon; count?: number }[];
}) {
  return (
    <TabsPrimitive.List className="mb-6 flex gap-6 border-b border-slate-200">
      {tabs.map((t) => (
        <TabsPrimitive.Trigger
          key={t.value}
          value={t.value}
          className={cn(
            "-mb-px inline-flex h-10 items-center gap-2 border-b-2 border-transparent px-0.5 text-sm font-medium text-slate-500 transition-colors hover:text-slate-800 data-[state=active]:border-brand-600 data-[state=active]:text-brand-800",
            focusRing,
          )}
        >
          {t.icon && <t.icon className="size-4" />}
          {t.label}
          {t.count !== undefined && (
            <span className="rounded-full bg-slate-100 px-1.5 py-px text-xs font-medium text-slate-600 tabular">
              {t.count}
            </span>
          )}
        </TabsPrimitive.Trigger>
      ))}
    </TabsPrimitive.List>
  );
}
